import React, { useState } from 'react';
import { Card, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import CustomerList from '../customers/CustomerList';
import { createEstimate, getNextOfferNumber } from '../../services/estimateService';
import './EstimateForm.css';

const EstimateForm = ({ onEstimateCreated, onCancel }) => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    customer_id: null,
    site_id: null,
    title: '',
    description: '',
    estimated_hours: '',
    estimated_cost: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);

  const handleSiteSelected = async (customer, site) => {
    setSelectedCustomer(customer);
    setSelectedSite(site);

    try {
      const offerNumber = await getNextOfferNumber();
      // Auto-generate title as "Customer Name + Offer Number" (e.g., "ABC Lucky 7945")
      const autoTitle = `${customer.name} ${offerNumber}`;
      // Format offer number with "O" suffix (e.g., "7945O")
      const formattedOfferNumber = `${offerNumber}O`;

      setFormData(prev => ({
        ...prev,
        customer_id: customer.id,
        site_id: site.id,
        title: autoTitle,
        reference_number: formattedOfferNumber
      }));
    } catch (err) {
      console.error('Error fetching offer number:', err);
      setFormData(prev => ({
        ...prev,
        customer_id: customer.id,
        site_id: site.id
      }));
    }

    setStep(2);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.customer_id || !formData.site_id) {
        throw new Error('Please select a customer and site.');
      }
      if (!formData.title || formData.title.trim().length === 0) {
        throw new Error('Estimate title is required.');
      }

      const submissionData = {
        ...formData,
        estimated_hours: formData.estimated_hours || null,
        estimated_cost: formData.estimated_cost || null,
        description: formData.description || null,
        notes: formData.notes || null
      };

      const result = await createEstimate(submissionData);

      if (result.reference_number) {
        setFormData(prev => ({ ...prev, reference_number: result.reference_number }));
      }

      navigate('/schedule?view=estimate&newEstimate=' + result.id);
    } catch (err) {
      console.error('[ESTIMATE FORM] Failed to create estimate:', err);

      let errorMessage = 'Failed to create estimate. Please try again.';
      if (err.message) errorMessage = err.message;
      else if (err.originalError?.message) errorMessage = err.originalError.message;

      if (err.retryCount !== undefined && err.retryCount > 0) {
        errorMessage += ` (Attempted ${err.retryCount + 1} times)`;
      }

      setError(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSelection = () => setStep(1);

  const handleCancel = () => {
    if (onCancel) onCancel();
    else if (onEstimateCreated) onEstimateCreated(null);
  };

  return (
    <Card className="estimate-form estimate-form__card">
      <Card.Header className="estimate-form__header">
        <h5 className="estimate-form__title">
          {step === 1 ? 'Select Customer & Site' : 'Create New Estimate'}
        </h5>
      </Card.Header>

      <Card.Body className="estimate-form__body">
        {step === 1 && (
          <>
            <p className="estimate-form__subtitle">
              Select a customer and site for this estimate:
            </p>
            <CustomerList selectionMode={true} onSelectSite={handleSiteSelected} />
          </>
        )}

        {step === 2 && (
          <>
            <section className="estimate-form__selected">
              <div className="estimate-form__selected-header">
                <h6 className="estimate-form__selected-title">Selected Customer & Site</h6>

                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleBackToSelection}
                  className="estimate-form__change-btn"
                >
                  <FaArrowLeft className="me-1" /> Change Selection
                </Button>
              </div>

              <Row className="g-3">
                <Col md={6}>
                  <div className="estimate-form__label">Customer</div>
                  <div className="estimate-form__value">{selectedCustomer?.name || 'N/A'}</div>
                </Col>

                <Col md={6}>
                  <div className="estimate-form__label">Site</div>
                  <div className="estimate-form__value">{selectedSite?.name || 'N/A'}</div>
                  <div className="estimate-form__muted">
                    {selectedSite?.address || ''}
                  </div>
                </Col>
              </Row>
            </section>

            {error && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => setError(null)}
                className="estimate-form__alert"
              >
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="estimate-form__field-label">
                      Estimate Title <span className="estimate-form__required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="e.g., Garage Door Replacement"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="estimate-form__field-label">Offer #</Form.Label>
                    <Form.Control
                      type="text"
                      name="reference_number"
                      value={formData.reference_number || ''}
                      onChange={handleChange}
                      placeholder="Auto-generated or enter custom"
                      className="estimate-form__offer"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mt-3">
                <Form.Label className="estimate-form__field-label">
                  Additional Description
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  placeholder="Detailed description of the work to be performed"
                />
              </Form.Group>

              <div className="estimate-form__info mt-3">
                <FaCalendarAlt className="estimate-form__info-icon" />
                <div className="estimate-form__info-text">
                  <strong>Ready to schedule?</strong> After creating this estimate,
                  you&apos;ll be taken to the scheduling calendar to set up the appointment.
                </div>
              </div>

              <div className="estimate-form__actions mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>

                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-2" /> Create & Schedule
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default EstimateForm;
